import React, { useState, useEffect } from 'react'
import { X, Settings, Globe, Bell, Shield, Zap, Database, Palette, RotateCcw, Save, AlertCircle, Upload, Trash2, ImageIcon } from 'lucide-react'
import { adminApi } from '../services/api'
import { resolveBrandingAssetUrl } from '../utils/brandingAssets'
import toast from 'react-hot-toast'

const TenantSettingsModal = ({ tenant, isOpen, onClose, onUpdate }) => {
  const [settings, setSettings] = useState({})
  const [originalSettings, setOriginalSettings] = useState({})
  const [activeTab, setActiveTab] = useState('general')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [assetUploading, setAssetUploading] = useState(false)

  const tabs = [
    { id: 'general', label: 'General', icon: Globe },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'features', label: 'Features', icon: Zap },
    { id: 'limits', label: 'Limits', icon: Database },
    { id: 'branding', label: 'Branding', icon: Palette }
  ]

  useEffect(() => {
    if (isOpen && tenant) {
      fetchSettings()
    }
  }, [isOpen, tenant])

  useEffect(() => {
    setHasChanges(JSON.stringify(settings) !== JSON.stringify(originalSettings))
  }, [settings, originalSettings])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      const response = await adminApi.getTenantSettings(tenant.id)
      setSettings(response.data.settings)
      setOriginalSettings(response.data.settings)
    } catch (error) {
      toast.error('Failed to load settings')
      console.error('Error fetching settings:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const response = await adminApi.updateTenantSettings(tenant.id, settings)
      setSettings(response.data.settings)
      setOriginalSettings(response.data.settings)
      toast.success('Settings saved successfully')

      if (onUpdate) {
        onUpdate()
      }
    } catch (error) {
      toast.error('Failed to save settings')
      console.error('Error saving settings:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    if (!confirm('Are you sure you want to reset all settings to defaults? This action cannot be undone.')) {
      return
    }

    try {
      setLoading(true)
      const response = await adminApi.resetTenantSettings(tenant.id)
      setSettings(response.data.settings)
      setOriginalSettings(response.data.settings)
      toast.success('Settings reset to defaults')

      if (onUpdate) {
        onUpdate()
      }
    } catch (error) {
      toast.error('Failed to reset settings')
      console.error('Error resetting settings:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDiscard = () => {
    setSettings(originalSettings)
    setHasChanges(false)
  }

  const updateSettings = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }))
  }

  const applySettingsFromServer = (nextSettings) => {
    setSettings(nextSettings)
    setOriginalSettings(nextSettings)
  }

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir görsel dosyası seçin (PNG, JPG, WEBP, SVG)')
      return
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo en fazla 2 MB olabilir')
      return
    }

    try {
      setAssetUploading(true)
      const response = await adminApi.uploadTenantLogo(tenant.id, file)
      applySettingsFromServer(response.data.settings)
      toast.success('Şirket logosu yüklendi')
      if (onUpdate) onUpdate()
    } catch (error) {
      toast.error(error.response?.data?.message || 'Logo yüklenemedi')
      console.error('Logo upload error:', error)
    } finally {
      setAssetUploading(false)
      event.target.value = ''
    }
  }

  const handleLogoDelete = async () => {
    if (!confirm('Şirket logosunu kaldırmak istediğinize emin misiniz?')) return

    try {
      setAssetUploading(true)
      const response = await adminApi.deleteTenantLogo(tenant.id)
      applySettingsFromServer(response.data.settings)
      toast.success('Logo kaldırıldı')
      if (onUpdate) onUpdate()
    } catch (error) {
      toast.error('Logo kaldırılamadı')
      console.error('Logo delete error:', error)
    } finally {
      setAssetUploading(false)
    }
  }

  const handleFaviconUpload = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/') && file.type !== 'image/x-icon') {
      toast.error('Lütfen bir favicon görseli seçin (ICO, PNG, SVG)')
      return
    }

    if (file.size > 512 * 1024) {
      toast.error('Favicon en fazla 512 KB olabilir')
      return
    }

    try {
      setAssetUploading(true)
      const response = await adminApi.uploadTenantFavicon(tenant.id, file)
      applySettingsFromServer(response.data.settings)
      toast.success('Favicon yüklendi')
      if (onUpdate) onUpdate()
    } catch (error) {
      toast.error(error.response?.data?.message || 'Favicon yüklenemedi')
      console.error('Favicon upload error:', error)
    } finally {
      setAssetUploading(false)
      event.target.value = ''
    }
  }

  const handleFaviconDelete = async () => {
    if (!confirm('Favicon kaldırılsın mı?')) return

    try {
      setAssetUploading(true)
      const response = await adminApi.deleteTenantFavicon(tenant.id)
      applySettingsFromServer(response.data.settings)
      toast.success('Favicon kaldırıldı')
      if (onUpdate) onUpdate()
    } catch (error) {
      toast.error('Favicon kaldırılamadı')
    } finally {
      setAssetUploading(false)
    }
  }

  const logoPreview = resolveBrandingAssetUrl(settings.branding, 'logo')
  const faviconPreview = resolveBrandingAssetUrl(settings.branding, 'favicon')
  const primaryColor = settings.branding?.primaryColor || '#3B82F6'
  const secondaryColor = settings.branding?.secondaryColor || '#64748B'

  const isPremiumFeature = (feature) => {
    return ['premium', 'enterprise'].includes(tenant.subscriptionPlan)
  }

  const isEnterpriseFeature = (feature) => {
    return tenant.subscriptionPlan === 'enterprise'
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Settings className="w-6 h-6 text-blue-600" />
              <div>
                <h3 className="text-lg font-medium text-gray-900">Tenant Settings</h3>
                <p className="text-sm text-gray-600">
                  {tenant.name} ({tenant.subdomain}) - {tenant.subscriptionPlan}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex">
          {/* Sidebar */}
          <div className="w-64 border-r border-gray-100 bg-secondary-50/50 p-4">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${activeTab === tab.id
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-secondary-700 hover:bg-secondary-50'
                      }`}
                  >
                    <Icon size={16} />
                    <span className="text-sm font-medium">{tab.label}</span>
                  </button>
                )
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                {/* General Settings */}
                {activeTab === 'general' && (
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4">General Settings</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Timezone
                          </label>
                          <select
                            value={settings.general?.timezone || 'UTC'}
                            onChange={(e) => updateSettings('general', 'timezone', e.target.value)}
                            className="input"
                          >
                            <option value="UTC">UTC</option>
                            <option value="America/New_York">Eastern Time</option>
                            <option value="America/Chicago">Central Time</option>
                            <option value="America/Denver">Mountain Time</option>
                            <option value="America/Los_Angeles">Pacific Time</option>
                            <option value="Europe/London">London</option>
                            <option value="Europe/Paris">Paris</option>
                            <option value="Asia/Tokyo">Tokyo</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Date Format
                          </label>
                          <select
                            value={settings.general?.dateFormat || 'YYYY-MM-DD'}
                            onChange={(e) => updateSettings('general', 'dateFormat', e.target.value)}
                            className="input"
                          >
                            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                            <option value="MM-DD-YYYY">MM-DD-YYYY</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Time Format
                          </label>
                          <select
                            value={settings.general?.timeFormat || '24h'}
                            onChange={(e) => updateSettings('general', 'timeFormat', e.target.value)}
                            className="input"
                          >
                            <option value="24h">24 Hour</option>
                            <option value="12h">12 Hour</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Currency
                          </label>
                          <select
                            value={settings.general?.currency || 'TRY'}
                            onChange={(e) => updateSettings('general', 'currency', e.target.value)}
                            className="input"
                          >
                            <option value="TRY">TRY - Turkish Lira</option>
                            <option value="USD">USD - US Dollar</option>
                            <option value="EUR">EUR - Euro</option>
                            <option value="GBP">GBP - British Pound</option>
                            <option value="JPY">JPY - Japanese Yen</option>
                            <option value="CAD">CAD - Canadian Dollar</option>
                            <option value="AUD">AUD - Australian Dollar</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Language
                          </label>
                          <select
                            value={settings.general?.language || 'en'}
                            onChange={(e) => updateSettings('general', 'language', e.target.value)}
                            className="input"
                          >
                            <option value="en">English</option>
                            <option value="es">Spanish</option>
                            <option value="fr">French</option>
                            <option value="de">German</option>
                            <option value="it">Italian</option>
                            <option value="pt">Portuguese</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notifications Settings */}
                {activeTab === 'notifications' && (
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Notification Settings</h4>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Email Notifications</label>
                            <p className="text-sm text-gray-500">Receive notifications via email</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={settings.notifications?.email || false}
                            onChange={(e) => updateSettings('notifications', 'email', e.target.checked)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Browser Notifications</label>
                            <p className="text-sm text-gray-500">Receive notifications in browser</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={settings.notifications?.browser || false}
                            onChange={(e) => updateSettings('notifications', 'browser', e.target.checked)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Mobile Notifications</label>
                            <p className="text-sm text-gray-500">Receive push notifications on mobile</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={settings.notifications?.mobile || false}
                            onChange={(e) => updateSettings('notifications', 'mobile', e.target.checked)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Webhook Notifications</label>
                            <p className="text-sm text-gray-500">Send notifications via webhooks</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={settings.notifications?.webhooks || false}
                            onChange={(e) => updateSettings('notifications', 'webhooks', e.target.checked)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Security Settings */}
                {activeTab === 'security' && (
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Security Settings</h4>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Two-Factor Authentication</label>
                            <p className="text-sm text-gray-500">
                              Require 2FA for all users
                              {!isPremiumFeature() && <span className="text-orange-600 ml-1">(Premium required)</span>}
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={settings.security?.twoFactorAuth || false}
                            onChange={(e) => updateSettings('security', 'twoFactorAuth', e.target.checked)}
                            disabled={!isPremiumFeature()}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">SSO Integration</label>
                            <p className="text-sm text-gray-500">
                              Enable Single Sign-On integration
                              {!isEnterpriseFeature() && <span className="text-orange-600 ml-1">(Enterprise required)</span>}
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={settings.security?.ssoIntegration || false}
                            onChange={(e) => updateSettings('security', 'ssoIntegration', e.target.checked)}
                            disabled={!isEnterpriseFeature()}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Session Timeout (minutes)
                          </label>
                          <input
                            type="number"
                            min="5"
                            max="480"
                            value={settings.security?.sessionTimeout || 30}
                            onChange={(e) => updateSettings('security', 'sessionTimeout', parseInt(e.target.value))}
                            className="input"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Password Policy
                          </label>
                          <select
                            value={settings.security?.passwordPolicy || 'medium'}
                            onChange={(e) => updateSettings('security', 'passwordPolicy', e.target.value)}
                            className="input"
                          >
                            <option value="low">Low - 6 characters minimum</option>
                            <option value="medium">Medium - 8 characters, mixed case</option>
                            <option value="high">High - 12 characters, mixed case, numbers, symbols</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Features Settings */}
                {activeTab === 'features' && (
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Feature Settings</h4>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Auto Backup</label>
                            <p className="text-sm text-gray-500">
                              Automatic daily backups
                              {tenant.subscriptionPlan === 'free' && <span className="text-orange-600 ml-1">(Basic+ required)</span>}
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={settings.features?.autoBackup || false}
                            onChange={(e) => updateSettings('features', 'autoBackup', e.target.checked)}
                            disabled={tenant.subscriptionPlan === 'free'}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">API Access</label>
                            <p className="text-sm text-gray-500">
                              Enable REST API access
                              {tenant.subscriptionPlan === 'free' && <span className="text-orange-600 ml-1">(Basic+ required)</span>}
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={settings.features?.apiAccess || false}
                            onChange={(e) => updateSettings('features', 'apiAccess', e.target.checked)}
                            disabled={tenant.subscriptionPlan === 'free'}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Webhook Integration</label>
                            <p className="text-sm text-gray-500">
                              Enable webhook integrations
                              {!isPremiumFeature() && <span className="text-orange-600 ml-1">(Premium+ required)</span>}
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={settings.features?.webhookIntegration || false}
                            onChange={(e) => updateSettings('features', 'webhookIntegration', e.target.checked)}
                            disabled={!isPremiumFeature()}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Audit Logs</label>
                            <p className="text-sm text-gray-500">
                              Enable detailed audit logging
                              {!isPremiumFeature() && <span className="text-orange-600 ml-1">(Premium+ required)</span>}
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={settings.features?.auditLogs || false}
                            onChange={(e) => updateSettings('features', 'auditLogs', e.target.checked)}
                            disabled={!isPremiumFeature()}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Limits Settings */}
                {activeTab === 'limits' && (
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Usage Limits</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Max Users
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={settings.limits?.maxUsers || 5}
                            onChange={(e) => updateSettings('limits', 'maxUsers', parseInt(e.target.value))}
                            className="input"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Plan limit: {tenant.subscriptionPlan === 'free' ? 5 : tenant.subscriptionPlan === 'basic' ? 25 : tenant.subscriptionPlan === 'premium' ? 100 : 1000}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Max Storage (GB)
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={Math.round((settings.limits?.maxStorage || 1073741824) / (1024 * 1024 * 1024))}
                            onChange={(e) => updateSettings('limits', 'maxStorage', e.target.value * 1024 * 1024 * 1024)}
                            className="input"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Plan limit: {tenant.subscriptionPlan === 'free' ? '1 GB' : tenant.subscriptionPlan === 'basic' ? '5 GB' : tenant.subscriptionPlan === 'premium' ? '50 GB' : '1 TB'}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Max Projects
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={settings.limits?.maxProjects || 3}
                            onChange={(e) => updateSettings('limits', 'maxProjects', parseInt(e.target.value))}
                            className="input"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Plan limit: {tenant.subscriptionPlan === 'free' ? 3 : tenant.subscriptionPlan === 'basic' ? 10 : tenant.subscriptionPlan === 'premium' ? 50 : 500}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Max API Calls/Day
                          </label>
                          <input
                            type="number"
                            min="100"
                            value={settings.limits?.maxApiCalls || 1000}
                            onChange={(e) => updateSettings('limits', 'maxApiCalls', parseInt(e.target.value))}
                            className="input"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Plan limit: {tenant.subscriptionPlan === 'free' ? '1K' : tenant.subscriptionPlan === 'basic' ? '10K' : tenant.subscriptionPlan === 'premium' ? '100K' : '1M'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Branding Settings */}
                {activeTab === 'branding' && (
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-2">Marka & Görsel</h4>
                      <p className="text-sm text-gray-500 mb-4">
                        Logo, favicon ve renkler yalnızca <strong>{tenant.name}</strong> kiracısına uygulanır.
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div className="rounded-lg border border-gray-200 p-4 bg-gray-50 space-y-3">
                          <label className="block text-sm font-medium text-gray-700">Şirket logosu</label>
                          <div className="h-24 flex items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white p-2">
                            {logoPreview ? (
                              <img src={logoPreview} alt="Logo önizleme" className="max-h-20 max-w-full object-contain" />
                            ) : (
                              <div className="flex flex-col items-center text-gray-400">
                                <ImageIcon className="h-8 w-8 mb-1" />
                                <span className="text-xs">Logo yok</span>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <label className={`btn btn-primary cursor-pointer inline-flex items-center gap-2 text-sm ${assetUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                              <Upload size={14} />
                              Logo yükle
                              <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={assetUploading} />
                            </label>
                            {logoPreview && (
                              <button type="button" onClick={handleLogoDelete} disabled={assetUploading} className="btn btn-secondary text-sm text-red-600">
                                <Trash2 size={14} className="inline mr-1" /> Kaldır
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="rounded-lg border border-gray-200 p-4 bg-gray-50 space-y-3">
                          <label className="block text-sm font-medium text-gray-700">Favicon</label>
                          <div className="h-24 flex items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white p-2">
                            {faviconPreview ? (
                              <img src={faviconPreview} alt="Favicon önizleme" className="max-h-12 max-w-12 object-contain" />
                            ) : (
                              <div className="flex flex-col items-center text-gray-400">
                                <ImageIcon className="h-6 w-6 mb-1" />
                                <span className="text-xs">Favicon yok</span>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <label className={`btn btn-primary cursor-pointer inline-flex items-center gap-2 text-sm ${assetUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                              <Upload size={14} />
                              Favicon yükle
                              <input type="file" accept="image/*,.ico" className="hidden" onChange={handleFaviconUpload} disabled={assetUploading} />
                            </label>
                            {faviconPreview && (
                              <button type="button" onClick={handleFaviconDelete} disabled={assetUploading} className="btn btn-secondary text-sm text-red-600">
                                <Trash2 size={14} className="inline mr-1" /> Kaldır
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">ICO, PNG, SVG · Maks. 512 KB</p>
                        </div>
                      </div>

                      <h4 className="text-lg font-medium text-gray-900 mb-3">Site renk teması</h4>
                      <p className="text-sm text-gray-500 mb-4">
                        Birincil renk butonlar ve vurgular; ikincil renk yardımcı öğeler için kullanılır. Kaydet&apos;e basınca kiracı uygulamasına yansır.
                      </p>

                      <div className="rounded-lg border border-gray-200 p-4 mb-4">
                        <p className="text-xs font-medium text-gray-500 mb-3">Canlı önizleme</p>
                        <div className="flex flex-wrap gap-3 items-center">
                          <button type="button" className="px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: primaryColor }}>
                            Birincil buton
                          </button>
                          <span className="text-sm font-medium" style={{ color: primaryColor }}>Birincil metin</span>
                          <span className="px-2 py-1 rounded text-xs text-white" style={{ backgroundColor: secondaryColor }}>İkincil</span>
                          <div className="h-8 w-8 rounded-full border-2" style={{ borderColor: primaryColor, backgroundColor: `${primaryColor}22` }} />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Birincil renk (Primary)</label>
                          <div className="flex items-center gap-3">
                            <input
                              type="color"
                              value={primaryColor}
                              onChange={(e) => updateSettings('branding', 'primaryColor', e.target.value)}
                              className="h-10 w-14 rounded border border-gray-300 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={primaryColor}
                              onChange={(e) => updateSettings('branding', 'primaryColor', e.target.value)}
                              className="input flex-1 font-mono text-sm"
                              pattern="^#[0-9A-Fa-f]{6}$"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">İkincil renk (Secondary)</label>
                          <div className="flex items-center gap-3">
                            <input
                              type="color"
                              value={secondaryColor}
                              onChange={(e) => updateSettings('branding', 'secondaryColor', e.target.value)}
                              className="h-10 w-14 rounded border border-gray-300 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={secondaryColor}
                              onChange={(e) => updateSettings('branding', 'secondaryColor', e.target.value)}
                              className="input flex-1 font-mono text-sm"
                              pattern="^#[0-9A-Fa-f]{6}$"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3 border-t border-gray-100 pt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Harici logo URL (isteğe bağlı)</label>
                          <input
                            type="url"
                            value={settings.branding?.logo || ''}
                            onChange={(e) => updateSettings('branding', 'logo', e.target.value)}
                            placeholder="https://example.com/logo.png"
                            className="input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Harici favicon URL (isteğe bağlı)</label>
                          <input
                            type="url"
                            value={settings.branding?.faviconUrl || ''}
                            onChange={(e) => updateSettings('branding', 'faviconUrl', e.target.value)}
                            placeholder="https://example.com/favicon.ico"
                            className="input"
                          />
                        </div>
                        {isPremiumFeature() && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Özel alan adı (Premium)</label>
                            <input
                              type="text"
                              value={settings.branding?.customDomain || ''}
                              onChange={(e) => updateSettings('branding', 'customDomain', e.target.value)}
                              placeholder="app.sirketiniz.com"
                              className="input"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleReset}
              disabled={loading}
              className="text-sm text-gray-600 hover:text-gray-700 disabled:opacity-50"
            >
              <RotateCcw size={16} className="inline mr-1" />
              Reset to Defaults
            </button>
            {hasChanges && (
              <button
                onClick={handleDiscard}
                className="text-sm text-gray-600 hover:text-gray-700"
              >
                Discard Changes
              </button>
            )}
          </div>

          <div className="flex items-center space-x-3">
            {hasChanges && (
              <div className="flex items-center text-sm text-orange-600">
                <AlertCircle size={16} className="mr-1" />
                Unsaved changes
              </div>
            )}
            <button
              onClick={onClose}
              className="btn btn-secondary"
            >
              Close
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="btn btn-primary disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TenantSettingsModal 